import { css } from "@compiled/react";

import StrokeWeightExtraLargeIcon from "@atlaskit/icon/core/stroke-weight-extra-large";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { DataEventProps } from "@/libraries/wpl/foundations/src/data-event-props";

import { getCurrRoute, useHeadingAtTop } from "../../utils";

export interface AnchorLinkProps {
    text: string;
    id: string;
}

export interface SideNavLinkProps {
    type: "link";
    text: string;
    href: string;
    dataEventProps?: DataEventProps;
    anchors?: AnchorLinkProps[];
    depth?: number;
    isAnchorLink?: boolean;
    isAnchorAtTop?: boolean;
}

export const SideNavLink = ({
    text,
    href,
    dataEventProps,
    anchors,
    depth = 0,
    isAnchorLink = false,
    isAnchorAtTop = false
}: // eslint-disable-next-line complexity
SideNavLinkProps) => {
    const localizedHref = useLocalizedHref(href);
    const { headingIdAtTop } = useHeadingAtTop(anchors);

    const currRoute = getCurrRoute();
    const isActive = currRoute === href;

    const shouldShowIcon =
        (isActive && !headingIdAtTop && !isAnchorLink) || isAnchorAtTop;
    const shouldBold = (isActive && !isAnchorLink) || isAnchorAtTop;

    return (
        <li
            css={[
                listStyleNoneCss,
                listItemCss,
                depth === 0 && css({ paddingBlock: token("space.200") })
            ]}
        >
            <a
                href={localizedHref}
                {...dataEventProps}
                css={[
                    navLinkCss,
                    depth > 0 && subLevelLinkCss,
                    !isActive && !isAnchorAtTop && navLinkHoverCss
                ]}
            >
                <div css={dotContainerCss}>
                    {shouldShowIcon && <StrokeWeightExtraLargeIcon label="" />}
                </div>
                <span
                    className={depth === 0 || shouldBold ? "h6" : "body-md"}
                    style={{
                        fontFamily: wacToken("font.charlie.text")
                    }}
                >
                    {text}
                </span>
            </a>
            {anchors && anchors.length > 0 && isActive && (
                <ul
                    css={[
                        listStyleNoneCss,
                        css({
                            paddingInlineStart: token("space.400")
                        })
                    ]}
                >
                    {anchors.map((anchor: AnchorLinkProps) => {
                        const anchorIsAtTop = headingIdAtTop === anchor.id;

                        return (
                            <SideNavLink
                                key={anchor.id}
                                dataEventProps={dataEventProps}
                                type="link"
                                href={`${localizedHref}#${anchor.id}`}
                                text={anchor.text}
                                depth={depth + 1}
                                isAnchorLink={true}
                                isAnchorAtTop={anchorIsAtTop}
                            />
                        );
                    })}
                </ul>
            )}
        </li>
    );
};

const listStyleNoneCss = css({
    listStyle: "none",
    margin: token("space.0"),
    padding: token("space.0")
});

const listItemCss = css({
    display: "block"
});

const navLinkCss = css({
    display: "flex",
    alignItems: "center"
});

const navLinkHoverCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "&:hover > span": {
        color: wacToken("color.primary.blue")
    }
});

const subLevelLinkCss = css({
    paddingBlock: token("space.100")
});

const dotContainerCss = css({
    width: "24px",
    height: "24px",
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    marginInlineEnd: token("space.050"),
    alignSelf: "flex-start"
});
